import React, { useContext, useEffect, useState } from 'react';

import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import NativeSelect from '@material-ui/core/NativeSelect';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

import {
  getCalcDescription,
  getCalcTemplates,
  setPricingModel,
} from '../../../Actions';
import { AlertContext } from '../../../Context/AlertContextProvider';

// "How Your Prices Are Calculated" on the Cost Adjustments page: a collapsible
// plain-English summary of the pricing calculation, generated server-side from
// the same graph the pricing engine runs (describe.js). A dropdown lets the
// admin preview the available pricing models (stock templates + graphs built
// for them) and switch the live model. Renders nothing for sites not on the
// custom calc engine ({ available: false }).

const noteStyle = {
  color: '#777',
  fontStyle: 'italic',
  fontSize: '0.78rem',
  display: 'block',
  marginTop: 2,
};

const StepList = ({ steps, nested }) => (
  <ol
    style={{
      margin: nested ? '4px 0 0' : '8px 0 0',
      paddingLeft: 22,
      listStyle: nested ? 'disc' : 'decimal',
    }}
  >
    {steps.map((step, i) => (
      <li key={i} style={{ marginBottom: 10 }}>
        {step.title ? (
          <Typography variant="body2" style={{ fontWeight: 600, lineHeight: 1.3 }}>
            {step.title}
          </Typography>
        ) : null}
        <Typography variant="body2" style={{ lineHeight: 1.35 }}>
          {step.text}
        </Typography>
        {step.note ? <span style={noteStyle}>{step.note}</span> : null}
        {(step.branches || []).map((branch, j) => (
          <div key={j} style={{ marginTop: 6, paddingLeft: 6, borderLeft: '2px solid #eee' }}>
            <Typography variant="body2" style={{ fontWeight: 600, color: '#555', paddingLeft: 6 }}>
              {branch.label}
            </Typography>
            <StepList steps={branch.steps} nested />
          </div>
        ))}
      </li>
    ))}
  </ol>
);

// 'margin' / 'markup' / 'graph:<id>' -> the description endpoint's source param.
const modelToSource = (model) => {
  if (/^graph:\d+$/.test(model)) return { graphId: Number(model.split(':')[1]) };

  return { template: model };
};

const CalculationSummary = () => {
  const { handleAlertOpen, setMessageType, setMessage } = useContext(AlertContext);

  const [liveStages, setLiveStages] = useState(null);
  const [models, setModels] = useState(null); // { templates, graphs, selection }
  const [selected, setSelected] = useState(null);
  const [previewStages, setPreviewStages] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // The live description doubles as the "is this site on custom calc" gate.
    getCalcDescription()
      .then((res) => {
        if (cancelled || !res.data?.available || !res.data.stages?.length) return;

        setLiveStages(res.data.stages);

        return getCalcTemplates().then(({ data }) => {
          if (cancelled || !data) return;

          setModels(data);
          setSelected(data.selection || null);
        });
      })
      .catch(() => {
        // Not on custom calc / endpoint unavailable — just don't render.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!liveStages) return null;

  const currentSelection = models?.selection || null;
  const isPreviewing = !!selected && selected !== currentSelection;
  const stages = isPreviewing ? previewStages : liveStages;

  const selectedName = (() => {
    if (!models || !selected) return '';

    const template = models.templates?.find((t) => t.key === selected);

    if (template) return template.name;

    const graph = models.graphs?.find((g) => `graph:${g.calc_graph_id}` === selected);

    return graph?.name || selected;
  })();

  const handleModelChange = (event) => {
    const model = event.target.value;

    setSelected(model);

    if (!model || model === currentSelection) {
      setPreviewStages(null);
      return;
    }

    setPreviewLoading(true);
    setPreviewStages(null);
    getCalcDescription(modelToSource(model))
      .then((res) => {
        if (res.data?.available) setPreviewStages(res.data.stages);
      })
      .catch(() => setPreviewStages(null))
      .finally(() => setPreviewLoading(false));
  };

  const handleActivate = () => {
    setSaving(true);
    setPricingModel(selected)
      .then(() => {
        setConfirmOpen(false);
        setModels((prev) => ({ ...prev, selection: selected }));
        setLiveStages(previewStages || liveStages);
        setPreviewStages(null);
        setMessageType('success');
        setMessage(`"${selectedName}" is now your pricing model.`);
        handleAlertOpen();
      })
      .catch(() => {
        setConfirmOpen(false);
        setMessageType('error');
        setMessage('Could not change the pricing model. Please try again.');
        handleAlertOpen();
      })
      .finally(() => setSaving(false));
  };

  return (
    <Accordion id="tour-calc-summary" elevation={5} style={{ margin: '0 10px 20px' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} id="tour-calc-summary-header">
        <div>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            How Your Prices Are Calculated
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Your pricing calculation, step by step — and the other pricing models you can switch to.
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails style={{ flexDirection: 'column', paddingTop: 0 }}>
        {models ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              flexWrap: 'wrap',
              marginBottom: 4,
            }}
          >
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              Pricing model:
            </Typography>
            <FormControl>
              <NativeSelect value={selected || ''} onChange={handleModelChange}>
                {!currentSelection ? <option value="">Current (unnamed)</option> : null}
                {(models.templates || []).map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.name}
                    {t.key === currentSelection ? ' (current)' : ''}
                  </option>
                ))}
                {(models.graphs || []).map((g) => (
                  <option key={g.calc_graph_id} value={`graph:${g.calc_graph_id}`}>
                    {g.name}
                    {`graph:${g.calc_graph_id}` === currentSelection ? ' (current)' : ''}
                  </option>
                ))}
              </NativeSelect>
            </FormControl>
            {isPreviewing ? (
              <>
                <Typography variant="body2" style={{ color: '#FF682C', fontWeight: 600 }}>
                  Preview — this is not your active pricing model.
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={previewLoading || !previewStages}
                  onClick={() => setConfirmOpen(true)}
                >
                  Use This Pricing Model
                </Button>
              </>
            ) : null}
          </div>
        ) : null}

        {previewLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </div>
        ) : null}

        {!previewLoading && stages ? (
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            {stages.map((stage, i) => (
              <Grid item xs={12} md={4} key={stage.group} style={{ display: 'flex' }}>
                <div
                  style={{
                    flex: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    padding: '12px 16px',
                    background: '#fafafa',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Typography variant="h6" style={{ color: '#FF682C', fontWeight: 600 }}>
                      {i + 1}. {stage.title}
                    </Typography>
                    {i < stages.length - 1 ? (
                      <ArrowForwardIcon fontSize="small" style={{ color: '#bbb' }} />
                    ) : null}
                  </div>
                  {stage.blurb ? (
                    <Typography variant="body2" color="textSecondary">
                      {stage.blurb}
                    </Typography>
                  ) : null}
                  <StepList steps={stage.steps} />
                </div>
              </Grid>
            ))}
          </Grid>
        ) : null}

        {!previewLoading && isPreviewing && !previewStages ? (
          <Typography variant="body2" color="textSecondary" style={{ padding: 16 }}>
            Couldn't load a preview for this pricing model.
          </Typography>
        ) : null}
      </AccordionDetails>

      <Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change your pricing model?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Every price in your showroom will be calculated with <b>{selectedName}</b> from
            the moment you confirm — on product pages, printed QR labels, and exports.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Your margins, taxes, and adjustments on this page keep their values; this only
            changes the steps they feed into. Your current model isn't deleted — you can
            switch back at any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleActivate} color="primary" variant="contained" disabled={saving}>
            {saving ? 'Switching…' : 'Yes, Use This Model'}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
};

export default CalculationSummary;
